import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  LifeBuoy,
  AlertCircle,
  Settings,
  FileQuestion,
  Bug,
  Zap,
  CheckCircle2,
  ArrowRight
} from 'lucide-react';
import FormSuccess from '../components/FormSuccess';

const serviceTypes = [
  {
    icon: Bug,
    title: 'Villa',
    description: 'Tilkynna villu eða vandamál'
  },
  {
    icon: FileQuestion,
    title: 'Fyrirspurn',
    description: 'Almennar spurningar um þjónustu'
  },
  {
    icon: Settings,
    title: 'Breytingabeiðni',
    description: 'Óska eftir breytingum á vefsíðu'
  },
  {
    icon: Zap,
    title: 'Uppfærsla',
    description: 'Uppfæra efni eða virkni'
  }
];

const ServiceDesk = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    website: '',
    problem: '',
    urgency: '',
    type: ''
  });
  const [showSuccess, setShowSuccess] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    
    try {
      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          'form-name': 'servicedesk',
          ...formData
        }).toString()
      });
      
      if (response.ok) {
        setShowSuccess(true);
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          website: '',
          problem: '',
          urgency: '',
          type: ''
        });
        form.reset();
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Villa kom upp við að senda form. Vinsamlegast reyndu aftur.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div 
          className="text-center max-w-3xl mx-auto mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#EA295A]/10 mb-6">
            <LifeBuoy className="w-8 h-8 text-[#EA295A]" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Þjónustuborð
          </h1>
          <p className="text-xl text-gray-600">
            Sendu okkur þjónustubeiðni eða fyrirspurn. Við svörum öllum beiðnum 
            eins fljótt og auðið er.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-12">
          {/* Service Request Form */}
          <motion.div 
            className="lg:col-span-2"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="bg-white rounded-2xl shadow-sm p-8">
              <form 
                name="servicedesk"
                method="POST"
                data-netlify="true"
                data-netlify-honeypot="bot-field"
                onSubmit={handleSubmit}
                className="space-y-6"
              >
                <input type="hidden" name="form-name" value="servicedesk" />
                <p hidden>
                  <label>
                    Don't fill this out: <input name="bot-field" />
                  </label>
                </p>

                {/* Service Type Selection */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">Tegund beiðni</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {serviceTypes.map((type, index) => (
                      <motion.div
                        key={index}
                        className={`p-4 border rounded-lg cursor-pointer transition-all ${
                          formData.type === type.title.toLowerCase()
                            ? 'border-[#EA295A] bg-[#EA295A]/5'
                            : 'border-gray-200 hover:border-[#EA295A]/50'
                        }`}
                        onClick={() => setFormData(prev => ({ ...prev, type: type.title.toLowerCase() }))}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <div className="flex items-start">
                          <div className="flex-shrink-0">
                            <type.icon className={`w-6 h-6 ${
                              formData.type === type.title.toLowerCase()
                                ? 'text-[#EA295A]'
                                : 'text-gray-400'
                            }`} />
                          </div>
                          <div className="ml-3">
                            <h4 className="text-sm font-medium text-gray-900">{type.title}</h4>
                            <p className="mt-1 text-xs text-gray-500">{type.description}</p>
                          </div>
                          {formData.type === type.title.toLowerCase() && (
                            <CheckCircle2 className="ml-auto w-5 h-5 text-[#EA295A]" />
                          )}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Contact Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">Upplýsingar</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                        Nafn
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#EA295A] focus:border-transparent"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                        Netfang
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#EA295A] focus:border-transparent"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                        Sími
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#EA295A] focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
                        Fyrirtæki
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#EA295A] focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>

                {/* Website Information */}
                <div>
                  <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-1">
                    Vefslóð
                  </label>
                  <input
                    type="url"
                    id="website"
                    name="website"
                    value={formData.website}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#EA295A] focus:border-transparent"
                    placeholder="https://example.com"
                  />
                </div>

                {/* Problem Description */}
                <div>
                  <label htmlFor="problem" className="block text-sm font-medium text-gray-700 mb-1">
                    Lýsing á vandamáli
                  </label>
                  <textarea
                    id="problem"
                    name="problem"
                    value={formData.problem}
                    onChange={handleInputChange}
                    rows={6}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#EA295A] focus:border-transparent"
                    required
                  />
                </div>

                {/* Urgency Level */}
                <div>
                  <label htmlFor="urgency" className="block text-sm font-medium text-gray-700 mb-1">
                    Forgangur
                  </label>
                  <select
                    id="urgency"
                    name="urgency"
                    value={formData.urgency}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#EA295A] focus:border-transparent"
                    required
                  >
                    <option value="">Veldu forgang</option>
                    <option value="low">Lágur</option>
                    <option value="medium">Miðlungs</option>
                    <option value="high">Hár</option>
                    <option value="urgent">Mjög hár</option>
                  </select>
                </div>

                <motion.button
                  type="submit"
                  className="w-full bg-[#EA295A] text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-[#d01d4c] transition-all shadow-lg shadow-[#EA295A]/20 hover:shadow-xl hover:shadow-[#EA295A]/30"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Senda beiðni
                </motion.button>
              </form>
            </div>
          </motion.div>

          {/* Info Sidebar */}
          <motion.div
            className="lg:col-span-1"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <div className="bg-white rounded-2xl shadow-sm p-8 sticky top-32">
              <div className="space-y-8">
                <div>
                  <div className="flex items-center mb-4">
                    <AlertCircle className="w-5 h-5 text-[#EA295A] mr-2" />
                    <h3 className="text-lg font-semibold text-gray-900">Mikilvægt</h3>
                  </div>
                  <p className="text-gray-600">
                    Vinsamlegast gefðu eins nákvæmar upplýsingar og mögulegt er 
                    svo við getum aðstoðað þig sem best.
                  </p>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Svartími</h4>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center text-gray-600">
                      <span className="w-3 h-3 bg-red-500 rounded-full mr-2"></span>
                      Mjög hár - innan 1 klst.
                    </li>
                    <li className="flex items-center text-gray-600">
                      <span className="w-3 h-3 bg-orange-500 rounded-full mr-2"></span>
                      Hár - innan 4 klst.
                    </li>
                    <li className="flex items-center text-gray-600">
                      <span className="w-3 h-3 bg-yellow-500 rounded-full mr-2"></span>
                      Miðlungs - innan 8 klst.
                    </li>
                    <li className="flex items-center text-gray-600">
                      <span className="w-3 h-3 bg-green-500 rounded-full mr-2"></span>
                      Lágur - innan 24 klst.
                    </li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Opnunartími</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Mánudagur - Föstudagur</span>
                      <span className="text-gray-900">9:00 - 17:00</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Laugardagur</span>
                      <span className="text-gray-900">Lokað</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Sunnudagur</span>
                      <span className="text-gray-900">Lokað</span>
                    </div>
                  </div>
                </div>

                <div className="pt-6 border-t border-gray-100">
                  <p className="text-sm text-gray-500">
                    Fyrir neyðartilvik utan opnunartíma, vinsamlegast hringið í 
                    síma (+354) 537-7990.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {showSuccess && (
        <FormSuccess 
          message="Takk fyrir beiðnina! Við munum hafa samband við þig eins fljótt og auðið er."
          onClose={() => setShowSuccess(false)}
        />
      )}
    </div>
  );
};

export default ServiceDesk;